/**
 * platform.js — native (Capacitor iOS) platform helpers.
 *
 * The native app has no subdomain, so the school is chosen once on first
 * launch and persisted with @capacitor/preferences. Because api.js computes
 * its baseURL synchronously at module-init, index.js loads the slug into
 * window.__WT_SCHOOL_SLUG__ BEFORE App (and therefore api.js) is imported.
 *
 * All exports are safe to call in the web build — they no-op / return null
 * when not running inside the native shell.
 */

import { Capacitor } from '@capacitor/core';

const SCHOOL_SLUG_KEY = 'welltrack_school_slug';
// Written by AppDelegate.recordIsMac() at launch, before the webview loads.
// Both halves must stay in sync — see the comment on `isMacKey` there.
const IS_MAC_KEY = 'wt_is_mac';
// Written by AppDelegate.applyManagedConfig() at launch, alongside the slug it
// copied out of Managed App Configuration. Same sync requirement as above.
const MANAGED_SCHOOL_KEY = 'wt_managed_school';
// The only key in this file written by JS and read by Swift rather than the
// other way round. See persistThemeForNative() below and
// AppDelegate.storedInterfaceStyle().
const THEME_KEY = 'wt_theme';

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

/**
 * True when this is the iOS binary running as a "Designed for iPad" app on an
 * Apple silicon Mac, rather than on a real iPhone or iPad.
 *
 * Detected by touchscreen, NOT by user agent and NOT by pointer capability. An
 * iPad-on-Mac reports an iPad user agent and Capacitor reports platform 'ios',
 * so neither distinguishes the two. Pointer capability doesn't either — an iPad
 * with a Magic Keyboard or a mouse reports `hover: hover` and `pointer: fine`
 * exactly like a Mac does. `maxTouchPoints` does: every iPhone and iPad reports
 * 5 whatever is plugged into it, and no Mac has a touchscreen, so 0 on a device
 * Capacitor calls iOS means the app is running on a Mac.
 *
 * Used only to choose wording ("password" vs "passcode", System Settings vs iOS
 * Settings) and never to decide whether to lock. A wrong answer here is a
 * cosmetic wrong, not a lockout — every capability decision reads the OS via
 * checkAvailability() instead.
 */
export function isMacDesktop() {
  if (!isNativeApp() || typeof navigator === 'undefined') return false;
  return (navigator.maxTouchPoints || 0) === 0;
}

/** Synchronous read — valid after bootstrapNative() has run (see index.js). */
export function getSchoolSlug() {
  return (typeof window !== 'undefined' && window.__WT_SCHOOL_SLUG__) || null;
}

/**
 * True only for the iPad binary running on an Apple silicon Mac.
 *
 * Synchronous, and valid after bootstrapNative() — same contract as
 * getSchoolSlug(). Sourced from ProcessInfo.isiOSAppOnMac in AppDelegate, never
 * sniffed: from inside the webview a Mac and an iPad are indistinguishable, and
 * the obvious heuristics all catch an iPad with a Magic Keyboard.
 */
export function isMacApp() {
  return (typeof window !== 'undefined' && window.__WT_IS_MAC__ === true);
}

/**
 * True when the school was supplied by MDM (Mosyle et al) rather than chosen in
 * SchoolPickerPage — i.e. a school-owned, centrally managed iPad.
 *
 * Only ever used to HIDE the "Switch school" controls. Switching is pointless
 * on these devices (AppDelegate re-applies the managed slug on the next launch,
 * so the change lasts exactly one session) and actively harmful on a shared
 * student iPad, where it turns a stray tap into "the app is broken" for whoever
 * picks it up next.
 *
 * Synchronous and valid after bootstrapNative(), same contract as
 * getSchoolSlug(). False on the web build and on any unmanaged device.
 */
export function isManagedSchool() {
  return (typeof window !== 'undefined' && window.__WT_MANAGED_SCHOOL__ === true);
}

/**
 * macOS hands a "Designed for iPad" webview a canvas 1.3x wider in CSS pixels
 * than the window physically is, so the whole UI paints at 77%. Measured
 * full-screen on a 1512pt display: innerWidth 1964 in the app against 1512 in
 * Safari, devicePixelRatio 2 in both (DPR does not expose the scaling, so it
 * cannot be derived at runtime).
 *
 * `initial-scale` rather than CSS. Three CSS attempts failed on device — see the
 * `.wt-mac` comment in index.css — because WebKit divides font-size by the
 * effective zoom and no `-webkit-text-size-adjust` value observed stopped it:
 * boxes scaled, text never did. `initial-scale` is composited by the platform,
 * which text has no way to opt out of, and it shrinks the layout viewport to
 * 1964 / 1.3 = 1511 CSS px — the width Safari lays out at the same physical
 * size, which is the whole objective.
 *
 * Unlike CSS zoom this is observable: `window.innerWidth` should read ~1511
 * afterwards, where under `zoom` it stayed 1964. That makes the fix
 * self-verifying rather than something to judge by eye.
 *
 * MAC_SCALE is constant because macOS applies a fixed ratio to these apps. If a
 * future macOS changes it, this is the only number to touch.
 */
const MAC_SCALE = 1.3;

function applyMacViewportScale() {
  const tag = document.querySelector('meta[name="viewport"]');
  if (!tag) return;
  // `width` is deliberately OMITTED, and that is the whole trick.
  //
  // The first version kept `width=device-width` alongside the scale. That pins
  // the layout viewport at the full 1964px while the scale shows only
  // 1964 / 1.3 = 1511px of it, so the text came out the right size and the last
  // KPI card and the Tier distribution panel fell off the right edge — a 453px
  // horizontal scroll, with the vertical 100% height chain overflowing for the
  // same reason.
  //
  // With no `width`, WebKit derives the layout viewport from the scale instead:
  // device-width / initial-scale. Layout and visible area then agree by
  // construction at any window size, so there is nothing to scroll and no second
  // number to keep in sync.
  //
  // viewport-fit=cover is preserved from index.html: the safe-area insets are 0
  // on a Mac, but the same string is what iPhone relies on and divergence here
  // would be invisible until someone tested a notch.
  tag.setAttribute('content', `initial-scale=${MAC_SCALE}, viewport-fit=cover`);
}

/**
 * Load the persisted school slug into window.__WT_SCHOOL_SLUG__.
 * Must complete before App is imported. Returns the slug (or null).
 */
export async function bootstrapNative() {
  if (!isNativeApp()) return null;
  // Marks the document as running inside the native shell so CSS can diverge
  // from the web build. Set before React renders so there is no flash of
  // web-only layout. See .wt-native rules in index.css.
  document.documentElement.classList.add('wt-native');
  const { Preferences } = await import('@capacitor/preferences');
  const { value } = await Preferences.get({ key: SCHOOL_SLUG_KEY });
  if (value) window.__WT_SCHOOL_SLUG__ = value;
  // Running as a "Designed for iPad" app on an Apple silicon Mac, which macOS
  // paints at 77% of the intended size. `.wt-mac` in index.css scales it back.
  // Read rather than sniffed because nothing visible to JS distinguishes this
  // from an iPad — see AppDelegate.recordIsMac() for what was ruled out.
  // A device that has never run the native half yet simply gets no class, which
  // is the current (unscaled) behaviour rather than a broken one.
  // Set by AppDelegate.applyManagedConfig() in the same launch pass that wrote
  // the slug above, so if this is 'true' the slug we just read is the MDM's.
  // Read here rather than lazily because LoginPage decides on its first render
  // whether to offer "Switch school", and an async read would flash the control
  // onto a managed device before removing it again.
  const { value: managed } = await Preferences.get({ key: MANAGED_SCHOOL_KEY });
  if (managed === 'true') window.__WT_MANAGED_SCHOOL__ = true;
  const { value: isMac } = await Preferences.get({ key: IS_MAC_KEY });
  if (isMac === 'true') {
    window.__WT_IS_MAC__ = true;
    document.documentElement.classList.add('wt-mac');
    applyMacViewportScale();
  }
  // App Lock preferences, for the same reason as the slug: BiometricGate has to
  // decide whether to render locked on its FIRST render, and an async read
  // would let the dashboard paint for a frame before the overlay appears.
  const { loadLockPrefs } = await import('./biometrics');
  await loadLockPrefs();
  return value || null;
}

/**
 * Hand the resolved theme ('dark' | 'default') to the native side.
 *
 * WHY THIS EXISTS
 *
 * AppDelegate paints the app-switcher privacy cover, and it has to paint it in
 * the user's theme. It cannot ask the webview: the cover goes up on
 * willResignActive, which is exactly the moment the app is being taken away, and
 * a round trip through the bridge is not something to start there. Nor can it
 * read the OS appearance and call it done — WellTrack's theme is an account
 * preference and 'default'/'dark' are explicit choices that need not agree with
 * iOS. That mismatch is the same one statusBar.js exists to fix; see the long
 * comment there for the two combinations it produces.
 *
 * So the theme is pushed OUT to UserDefaults whenever it is applied, and the
 * native side reads the last value it was given. Same @capacitor/preferences
 * store, and therefore the same "CapacitorStorage." prefix, as the school slug
 * and the Mac flag — the difference is only the direction of travel.
 *
 * The value is deliberately the RESOLVED theme, not the raw preference: 'system'
 * would leave Swift to resolve it, which means duplicating resolve() natively
 * and keeping the two in step. The mq listener in ThemeContext already re-calls
 * applyTheme when the OS flips, so a user on 'system' gets a fresh value written
 * at the moment it changes.
 *
 * Fire-and-forget and never throws, for the same reason as syncStatusBarStyle:
 * it is called from ThemeProvider's mount effect, and a failed write is a cover
 * in last session's colours — not something that may take the first render down.
 */
export async function persistThemeForNative(resolvedTheme) {
  if (!isNativeApp()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    // A plain string, not a boolean: @capacitor/preferences stores strings only,
    // and the Swift side reads it with string(forKey:).
    await Preferences.set({
      key: THEME_KEY,
      value: resolvedTheme === 'dark' ? 'dark' : 'default',
    });
  } catch { /* plugin absent, or web build — native falls back to the OS style */ }
}

export async function setSchoolSlug(slug) {
  const { Preferences } = await import('@capacitor/preferences');
  await Preferences.set({ key: SCHOOL_SLUG_KEY, value: slug });
  window.__WT_SCHOOL_SLUG__ = slug;
}

export async function clearSchoolSlug() {
  const { Preferences } = await import('@capacitor/preferences');
  await Preferences.remove({ key: SCHOOL_SLUG_KEY });
  delete window.__WT_SCHOOL_SLUG__;
}
